import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { V2Card } from '@abyss/web/ui/Card/v2';
import { Carousel, Slide } from '@abyss/web/ui/Carousel';
import { Divider } from '@abyss/web/ui/Divider';
import { V2EmphasisBanner } from '@abyss/web/ui/EmphasisBanner';
import { Flex } from '@abyss/web/ui/Flex';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../../common/ConstantsLagoon';
import { mobileOnly } from '../../../../../common/ConstantsStyles';
import { makeAllSlidesAriaHidden } from '../../../../../common/Utils/a11yUtils/a11yUtils';
import { useLagoon } from '../../../../../hooks/useLagoon';
import {
  getAllProvidersWithServiceType,
  setVirtualCareListViewPageParam,
} from '../utils';
import { VirtualCareProviderCard } from './VirtualCareProviderCard';
import {
  carouselStyle,
  emphasisBannerStyle,
  slideContainerStyle,
  slideStyle,
  viewAllCardCss,
  viewAllLinkCss,
  virtualCareBannerContentStyle,
  virtualCareBannerHeaderStyle,
  virtualCareBannerLinkStyle,
} from './VirtualCareProvidersBanner.styled';
import { VirtualCareProviderModal } from './VirtualCareProvidersModal';

type VirtualCareProvidersBannerProps = {
  virtualCareProviders: any;
  setVirtualCareListView: (a: boolean) => void;
  expandedProviderListView: boolean;
};

export const VirtualCareProvidersBanner = ({
  expandedProviderListView = false,
  virtualCareProviders,
  setVirtualCareListView,
}: VirtualCareProvidersBannerProps) => {
  const [visibleAlerts, setVisibleAlerts] = useState([true, true]);
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProviderName, setSelectedProviderName] = useState<
    string | null
  >(null);
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(mobileOnly);
  const data = useLagoon('ui-messaging')();
  const singleProviderContent = find(data, {
    key: ConstantsLagoon.VIRTUAL_CARE_PROVIDERS_BANNER
      .VIRTUAL_CARE_PROVIDERS_BANNER_CONTENT_FOR_ONE,
  });
  const bannerContent = find(data, {
    key: ConstantsLagoon.VIRTUAL_CARE_PROVIDERS_BANNER
      .VIRTUAL_CARE_PROVIDERS_BANNER_CONTENT,
  });
  const linkContent = find(data, {
    key: ConstantsLagoon.VIRTUAL_CARE_PROVIDERS_BANNER
      .VIRTUAL_CARE_PROVIDERS_BANNER_LINK,
  });
  const allProviders = getAllProvidersWithServiceType(virtualCareProviders);

  const virtualCareProviderCount = allProviders?.length;
  const bannerMessage =
    (virtualCareProviderCount > 1
      ? bannerContent?.message
      : singleProviderContent?.message
    )?.replace('${count}', virtualCareProviderCount) || '';

  const slides = [
    ...allProviders?.slice(0, 3).map((provider) => (
      <Slide css={slideStyle} key="slide">
        <Slide.Container css={slideContainerStyle} key="slide">
          <VirtualCareProviderCard
            key={provider?.providerName}
            mapView={!expandedProviderListView}
            mobileScreen={mobileScreen}
            provider={provider}
            serviceType={
              virtualCareProviders?.serviceType || provider?.serviceType
            }
            setIsModalOpen={setIsModalOpen}
            setSelectedProviderName={setSelectedProviderName}
          />
        </Slide.Container>
      </Slide>
    )),
  ];

  if (virtualCareProviderCount > 3) {
    slides.push(
      <V2Card
        css={viewAllCardCss(mobileScreen || !expandedProviderListView)}
        key="all-virtual-care-link"
        onClick={(e) => {
          e.preventDefault();
          // Update the URL with the new parameter
          const url = new URL(window.location.href);
          setVirtualCareListViewPageParam(url);
          setVirtualCareListView(true);
        }}
      >
        <Link
          after={<IconSymbol icon="chevron_right" size="20" />}
          css={viewAllLinkCss}
          data-auto-testid={`view-all-virtual-care-providers-link-text`}
          data-testid={`view-all-virtual-care-providers-link-text`}
        >
          {t('VIRTUAL_CARE_PROVIDERS_BANNER.ALL_VIRTUAL_CARE')}
        </Link>
      </V2Card>
    );
  }

  const bannerContentNode = (
    <Flex css={virtualCareBannerContentStyle} direction="column">
      <Flex
        css={virtualCareBannerHeaderStyle(
          mobileScreen,
          expandedProviderListView
        )}
      >
        <Text
          css={{
            'abyss-text-root': {
              color: '#323333',
              fontSize: '18px',
              fontWeight: 700,
              lineHeight: '22px',
            },
          }}
          data-testid="virtual-care-provider-banner-title"
          size="$lg"
        >
          {bannerContent?.title}
        </Text>
        <Text
          css={{
            'abyss-text-root': {
              color: '#323333',
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '18px',
            },
          }}
          data-testid="virtual-care-provider-banner-description"
        >
          {bannerMessage}
        </Text>
      </Flex>
      <Link
        css={virtualCareBannerLinkStyle}
        data-auto-testid="virtual-care-provider-link-text"
        data-testid="virtual-care-provider-link-text"
        onClick={(e) => {
          e.preventDefault();
          setIsOpen((prev) => !prev);
        }}
      >
        {linkContent?.title}
        <IconMaterial
          css={{ marginLeft: '4px' }}
          icon={isOpen ? 'expand_less' : 'expand_more'}
          size="20"
        />
      </Link>
      {isOpen &&
        (mobileScreen ||
        (!expandedProviderListView && virtualCareProviderCount > 2) ? (
          <Carousel
            css={carouselStyle(true)}
            data-auto-testid="virtual-care-provider-carousel"
            data-testid="virtual-care-provider-carousel"
            minimal
            nextSlideOnClick={() => makeAllSlidesAriaHidden()}
            noLoop
            prevSlideOnClick={() => makeAllSlidesAriaHidden()}
            slideIndexOnClick={() => makeAllSlidesAriaHidden()}
            slides={slides}
            slidesPerView={1}
          />
        ) : (
          <Flex
            css={{
              width: '100%',
              gap: '16px',
              flexWrap: 'nowrap',
              overflow: 'hidden',
            }}
            direction="row"
          >
            {slides?.map((slide, index) => (
              <Flex
                css={{
                  flex: '0 0 auto',
                  marginBottom: '8px',
                }}
                key={`slide-${index}`}
              >
                {slide}
              </Flex>
            ))}
          </Flex>
        ))}
    </Flex>
  );

  return (
    <React.Fragment>
      {visibleAlerts[0] && (
        <V2EmphasisBanner
          color="emphasis4"
          css={emphasisBannerStyle(mobileScreen)}
          customContent={bannerContentNode}
          data-testid="virtual-care-provider-banner"
          iconBrand={{
            icon: 'virtual_care',
            variant: 'twotone',
          }}
          onClose={() => {
            setVisibleAlerts([false, visibleAlerts[1]]);
          }}
        />
      )}
      <VirtualCareProviderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedProviderName={selectedProviderName}
        virtualCareProviders={virtualCareProviders}
      />
      {expandedProviderListView && <Divider color="$gray2" />}
    </React.Fragment>
  );
};
